import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate, Link } from "react-router-dom";

import FrontendLayout from "../../../components/frontend/FrontendLayout";
import UserCommon from "../../../components/frontend/UserCommon";
import UserTabs from "../../../components/frontend/UserTabs";
import FormatIndianPrice from "../../../components/frontend/FormatIndianPrice";
import { formatShortPrice } from "../../../utils/formatNumber";
import { formatNumberIndian } from "../../../utils/formatNumber";
import { handleApiError } from "../../../utils/apiHelpers";
import { ToastContainer, toast } from "react-toastify";
import { useUserProfile } from "../../../hooks/useUserProfile";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

interface LandDetail {
  ln_id: number;
  ln_landno: string;
  ln_title: string;
  ln_price: number;
  ln_total_area_sqft: number;
  ln_total_sqft_area: string;
  ln_post_date: string;
  ln_plottype: string;
  ln_anyconstruction: string;
  ln_legal_status: string;
  ln_infrastructural_status: string;
  ln_boundarywall: string;
  ln_overlooking: string;
  ln_transaction_type: string;
  ln_locality: string;
  ln_address: string;
  ln_width_roadfacing: number;
  ln_desc: string;
  ln_hilights: string;
  ln_possesion_status: string;
  ln_map_long: string;
  ln_map_lat: string;
  ln_seller_type: string;
  ln_zone: string;
  ln_growth: string;
  ln_gated: string;
  ln_loan: string;
  ln_land_avail: string;
  ln_classification: string;
  ln_facing: string;
  ln_corner: string;
  ln_fencing_type: string;
  ln_electric_supply: string;
  ln_water_supply: string;
  ln_nearby_landmark: string;
  ln_topography: string;
  ln_water_looging: string;
  ln_rerano: string;
  ln_authority_approve: string;
  ln_encumbrance_status: string;
  ln_mutation: string;
  ln_patta_avail: string;
  ln_sitevisit_avail: string;
  ln_sitevisit_link: string;
  ln_soiltype: string;
  ln_featured: string;
  ln_dimension_east: string;
  ln_dimension_west: string;
  ln_dimension_north: string;
  ln_dimension_south: string;
  ln_allowed_floors: number;
  ln_image: string[];
  ln_video: string[];
  ln_encumb_cert: string[];
  ln_drone_vid: string[];
}

const LandView: React.FC = () => {
  const [land, setLand] = useState<LandDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const token = localStorage.getItem("frontendUserToken");
  const userType = localStorage.getItem("frontendUserType");

  const profile = useUserProfile(token);

  const fetchLandDetails = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${API_BASE}/api/landadd/edit/${id}?userid=${profile?.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.data.success) {
        setLand(res.data.landadd);
      } else {
        toast.error("Failed to fetch land details");
        navigate("/user/lands");
      }
    } catch (err: any) {
      if (axios.isAxiosError(err) && err.response?.status === 403) {
        toast.error("You do not have permission to view this land");
        navigate("/403");
        return;
      }
      handleApiError(err, navigate);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userType !== "1" && userType !== "2") {
      navigate("/403");
      return;
    }
    if (id) {
      fetchLandDetails();
    }
  }, [id, userType, navigate]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const handleCertificateDownload = async (certFileName: string) => {
    try {
      const response = await fetch(`${API_BASE}/uploads/lands/${certFileName}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = certFileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.dismiss();
      toast.success("Encumbarance Certificate downloaded successfully!");
    } catch (error) {
      console.error("Error downloading certificate:", error);
      toast.error("Failed to download certificate.");
    }
  };

  const renderMediaGallery = () => {
    const mediaItems: React.ReactElement[] = [];

    // Add images
    if (land?.ln_image && land.ln_image.length > 0) {
      land.ln_image.forEach((image, index) => {
        mediaItems.push(
          <div className="col-lg-2 mb-4" key={`image-${index}`}>
            <a
              href={`${API_BASE}/uploads/lands/${image}`}
              data-fancybox="gallery"
              className="image-wrap d-block"
            >
              <img
                className="lazyload"
                src={`${API_BASE}/uploads/lands/${image}`}
                alt={`Land image ${index + 1}`}
              />
            </a>
          </div>
        );
      });
    } else {
      mediaItems.push(
        <div className="col-lg-2 mb-4" key="image-placeholder">
          <a
            href={`${process.env.REACT_APP_STATIC_BASE_URL}/frontend/images/No-Image-Placeholder.jpg`}
            data-fancybox="gallery"
            className="image-wrap d-block"
          >
            <img
              className="lazyload"
              src={`${process.env.REACT_APP_STATIC_BASE_URL}/frontend/images/No-Image-Placeholder.jpg`}
              alt="No Image Available"
            />
          </a>
        </div>
      );
    }

    // Add videos
    if (land?.ln_video && land.ln_video.length > 0) {
      land.ln_video.forEach((video, index) => {
        mediaItems.push(
          <div className="col-lg-2 mb-4" key={`video-${index}`}>
            <a
              href={`${API_BASE}/uploads/lands/${video}`}
              data-fancybox="gallery"
              data-caption="Land video"
              className="image-wrap d-block"
            >
              <img
                className="lazyload"
                src={`${process.env.REACT_APP_STATIC_BASE_URL}/frontend/video-play-btn-featured.png`}
                alt="Video thumbnail"
              />
            </a>
          </div>
        );
      });
    }

    // Add drone videos
    if (land?.ln_drone_vid && land.ln_drone_vid.length > 0) {
      land.ln_drone_vid.forEach((droneVideo, index) => {
        mediaItems.push(
          <div className="col-lg-2 mb-4" key={`drone-${index}`}>
            <a
              href={`${API_BASE}/uploads/lands/${droneVideo}`}
              data-fancybox="gallery"
              data-caption="Drone video"
              className="image-wrap d-block"
            >
              <img
                className="lazyload"
                src={`${process.env.REACT_APP_STATIC_BASE_URL}/frontend/video-play-btn-featured.png`}
                alt="Drone video thumbnail"
              />
            </a>
          </div>
        );
      });
    }

    if (land?.ln_encumb_cert && land.ln_encumb_cert.length > 0) {
      land.ln_encumb_cert.forEach((cert: string, index: number) => {
        mediaItems.push(
          <div className="col-md-3 col-6" key={`cert-${index}`}>
            <div
              className="certificate-download-container"
              style={{
                cursor: "pointer",
                border: "2px dashed #007bff",
                borderRadius: "8px",
                padding: "28px",
                textAlign: "center",
                backgroundColor: "#f8f9fa",
                transition: "all 0.3s ease",
              }}
              onClick={() => handleCertificateDownload(cert)}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#e9ecef";
                e.currentTarget.style.borderColor = "#0056b3";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#f8f9fa";
                e.currentTarget.style.borderColor = "#007bff";
              }}
            >
              <div
                style={{
                  fontSize: "2rem",
                  color: "#007bff",
                  marginBottom: "10px",
                }}
              >
                📄
              </div>
              <p style={{ margin: 0, fontWeight: "bold", color: "#007bff" }}>
                Encumbarance Certificate
              </p>
              <p style={{ margin: 0, fontSize: "0.8rem", color: "#6c757d" }}>
                Click to download
              </p>
            </div>
          </div>
        );
      });
    }

    return mediaItems;
  };

  if (loading) {
    return (
      <FrontendLayout>
        <div className="user">
          <div className="tf-container py-5 user">
            <div className="text-center">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          </div>
        </div>
      </FrontendLayout>
    );
  }

  if (!land) {
    return (
      <FrontendLayout>
        <div className="user">
          <div className="tf-container py-5 user">
            <div className="text-center">
              <h4>Land not found</h4>
              <button
                className="btn btn-primary"
                onClick={() => navigate("/user/lands")}
              >
                Back to My Lands
              </button>
            </div>
          </div>
        </div>
      </FrontendLayout>
    );
  }

  return (
    <FrontendLayout>
      <ToastContainer />
      <div className="user">
        <div className="tf-container py-5 user">
          <UserCommon
            name={profile.name}
            email={profile.email}
            imageUrl={profile.imageUrl}
            userType={profile?.userType}
          />

          <div className="tab" role="tabpanel">
            <UserTabs />

            <div className="tab-content tabs">
              <div role="tabpanel" className="tab-pane in active">
                <div className="row">
                  <div className="col-lg-12">
                    <div className="box-overview">
                      <div className="heading flex justify-between pb-3 mb-4">
                        <div className="title text-8 fw-6 text-color-heading">
                          {land.ln_title}
                        </div>
                        <div className="price text-8 fw-6 text-color-heading">
                          <i className="bi bi-currency-rupee me-1"></i>
                          {formatShortPrice(land.ln_price)}
                          <span className="h6 lh-30 fw-4 text-color-default ms-2">
                            <i className="bi bi-currency-rupee me-1"></i>
                            {formatNumberIndian(land.ln_total_area_sqft)}
                            /sqft
                          </span>
                        </div>
                      </div>

                      <div className="info flex justify-between">
                        <div className="feature">
                          <h6 className="location text-1 flex items-center gap-10">
                            <i className="icon-location"></i>
                            {land.ln_locality}, {land.ln_address}
                          </h6>
                        </div>
                        <div className="feature">
                          <ul className="meta-list flex">
                            <li className="text-1 flex">
                              Posted on {formatDate(land.ln_post_date)}
                            </li>
                          </ul>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-lg-3 pb-5">
                          <div className="box-icon">
                            <div className="icons sec2">
                              <i className="fa fa-expand"></i>
                            </div>
                            <div className="content">
                              <div className="text-4 text-color-default">
                                Plot Area
                              </div>
                              <div className="text-1 text-color-heading">
                                {land.ln_total_sqft_area} sqft
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-lg-3 pb-5">
                          <div className="box-icon">
                            <div className="icons sec2">
                              <i className="fa fa-border-style"></i>
                            </div>
                            <div className="content">
                              <div className="text-4 text-color-default">
                                Zone
                              </div>
                              <div className="text-1 text-color-heading">
                                {land.ln_zone}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-lg-3 pb-5">
                          <div className="box-icon">
                            <div className="icons sec2">
                              <i className="fa fa-compass"></i>
                            </div>
                            <div className="content">
                              <div className="text-4 text-color-default">
                                Facing
                              </div>
                              <div className="text-1 text-color-heading">
                                {land.ln_facing}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-lg-3 pb-5">
                          <div className="box-icon">
                            <div className="icons sec2">
                              <i className="fa fa-th-large"></i>
                            </div>
                            <div className="content">
                              <div className="text-4 text-color-default">
                                Boundary Wall
                              </div>
                              <div className="text-1 text-color-heading">
                                {land.ln_boundarywall
                                  ? land.ln_boundarywall
                                  : "--"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-lg-3 pb-5">
                          <div className="box-icon">
                            <div className="icons sec2">
                              <i className="fa fa-star"></i>
                            </div>
                            <div className="content">
                              <div className="text-4 text-color-default">
                                Featured plot
                              </div>
                              <div className="text-1 text-color-heading">
                                {land.ln_featured === "Yes" ? "Yes" : "No"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-lg-3 pb-5">
                          <div className="box-icon">
                            <div className="icons sec2">
                              <i className="fa fa-user"></i>
                            </div>
                            <div className="content">
                              <div className="text-4 text-color-default">
                                Seller Type
                              </div>
                              <div className="text-1 text-color-heading">
                                {land.ln_seller_type.charAt(0).toUpperCase() +
                                  land.ln_seller_type.slice(1)}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-lg-3 pb-5">
                          <div className="box-icon">
                            <div className="icons sec2">
                              <i className="fa fa-key"></i>
                            </div>
                            <div className="content">
                              <div className="text-4 text-color-default">
                                Possession Status
                              </div>
                              <div className="text-1 text-color-heading">
                                {land.ln_possesion_status
                                  ? land.ln_possesion_status
                                  : "--"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-lg-3 pb-5">
                          <div className="box-icon">
                            <div className="icons sec2">
                              <i className="fa fa-crop"></i>
                            </div>
                            <div className="content">
                              <div className="text-4 text-color-default">
                                Corner Plot
                              </div>
                              <div className="text-1 text-color-heading">
                                {land.ln_corner}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <hr className="mb-3" />

                <div className="row plot-gallery">
                  <div className="col-lg-12 mb-4">
                    <div className="wg-title text-1 fw-6 text-color-heading mb-1">
                      Plot Files
                    </div>
                  </div>
                  {renderMediaGallery()}
                </div>

                <hr className="mb-3" />

                <div className="row">
                  <div className="col-lg-12 pb-5">
                    <div className="wg-property box-property-detail spacing-1 mb-3">
                      <div className="wg-title text-1 fw-6 text-color-heading mb-3">
                        Plot Description
                      </div>
                      <div className="content">
                        <p className="description text-9 mb-10">
                          {land.ln_desc}
                        </p>
                      </div>
                      <div className="box">
                        <ul>
                          <li>
                            <p className="fw-6">Transaction Type (Resale)</p>
                            <p>{land.ln_transaction_type}</p>
                          </li>
                          <li>
                            <p className="fw-6">Width of Road Facing</p>
                            <p>{land.ln_width_roadfacing} ft</p>
                          </li>
                          <li>
                            <p className="fw-6">Overlooking</p>
                            <p>
                              {land.ln_overlooking ? land.ln_overlooking : "--"}
                            </p>
                          </li>
                          <li>
                            <p className="fw-6">No. of Floors Allowed</p>
                            <p>
                              {land.ln_allowed_floors !== null &&
                              land.ln_allowed_floors !== undefined
                                ? land.ln_allowed_floors
                                : "--"}
                            </p>
                          </li>
                          <li>
                            <p className="fw-6">Fencing Type</p>
                            <p>
                              {land.ln_fencing_type
                                ? land.ln_fencing_type
                                : "--"}
                            </p>
                          </li>
                          <li>
                            <p className="fw-6">Classification</p>
                            <p>{land.ln_classification}</p>
                          </li>
                          <li>
                            <p className="fw-6">Nearby Landmark</p>
                            <p>{land.ln_nearby_landmark}</p>
                          </li>
                        </ul>
                        <ul>
                          <li>
                            <p className="fw-6">Gated Society</p>
                            <p>{land.ln_gated}</p>
                          </li>
                          <li>
                            <p className="fw-6">
                              Electricity & Water Supply Availability
                            </p>
                            <p>
                              {land.ln_electric_supply} & {land.ln_water_supply}
                            </p>
                          </li>
                          <li>
                            <p className="fw-6">Topography</p>
                            <p>
                              {land.ln_topography ? land.ln_topography : "--"}
                            </p>
                          </li>
                          <li>
                            <p className="fw-6">
                              Flood Zone Risk / Water Logging
                            </p>
                            <p>
                              {land.ln_water_looging
                                ? land.ln_water_looging
                                : "--"}
                            </p>
                          </li>
                          <li>
                            <p className="fw-6">Authority Approved</p>
                            <p>{land.ln_authority_approve}</p>
                          </li>
                          <li>
                            <p className="fw-6">Mutation Done</p>
                            <p>{land.ln_mutation}</p>
                          </li>
                        </ul>
                        <ul>
                          <li>
                            <p className="fw-6">Patta Available</p>
                            <p>
                              {land.ln_patta_avail ? land.ln_patta_avail : "--"}
                            </p>
                          </li>
                          <li>
                            <p className="fw-6">Soil Type</p>
                            <p>
                              {land.ln_soiltype
                                ? land.ln_soiltype.charAt(0).toUpperCase() +
                                  land.ln_soiltype.slice(1)
                                : "--"}
                            </p>
                          </li>
                          <li>
                            <p className="fw-6">Status</p>
                            <p>{land.ln_legal_status}</p>
                          </li>
                          <li>
                            <p className="fw-6">Loan Available</p>
                            <p>{land.ln_loan ? land.ln_loan : "--"}</p>
                          </li>
                          <li>
                            <p className="fw-6">Land Availability</p>
                            <p>
                              {land.ln_land_avail === "Avail"
                                ? "Available"
                                : "Sold Out"}
                            </p>
                          </li>
                          <li>
                            <p className="fw-6">Site Visit Available</p>
                            <p>{land.ln_sitevisit_avail}</p>
                          </li>
                          {land.ln_sitevisit_link && (
                            <li>
                              <p className="fw-6">Site Visit Link</p>
                              <p>
                                <a
                                  href={land.ln_sitevisit_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  View Map
                                </a>
                              </p>
                            </li>
                          )}
                          <li>
                            <p className="fw-6">Address</p>
                            <p>{land.ln_address}</p>
                          </li>
                        </ul>
                      </div>

                      <div className="wg-title text-1 fw-6 text-color-heading mb-3 mt-5">
                        Plot Dimensions in Feet
                      </div>
                      <div className="box">
                        <div className="row">
                          <div className="col-lg-3">
                            <p className="fw-6">East</p>
                            <p>{land.ln_dimension_east}</p>
                          </div>
                          <div className="col-lg-3">
                            <p className="fw-6">West</p>
                            <p>{land.ln_dimension_west}</p>
                          </div>
                          <div className="col-lg-3">
                            <p className="fw-6">North</p>
                            <p>{land.ln_dimension_north}</p>
                          </div>
                          <div className="col-lg-3">
                            <p className="fw-6">South</p>
                            <p>{land.ln_dimension_south}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="wg-property box-property-detail spacing-1 pb-5 mb-3">
                      <div className="wg-title text-1 fw-6 text-color-heading mb-3">
                        Growth Potentials (upcoming features for that plot/land)
                      </div>
                      <div className="content mb-0">
                        <p className="description text-9 mb-10">
                          {land.ln_growth ? land.ln_growth : "--"}
                        </p>
                      </div>
                    </div>

                    <div className="wg-property box-amenities spacing-3">
                      <div className="wg-title text-1 fw-6 text-color-heading mb-4">
                        Highlights
                      </div>
                      <div className="wrap-feature">
                        <div className="box-feature">
                          <div className="description text-9">
                            {land.ln_hilights ? land.ln_hilights : "--"}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="box">
                  <div className="d-flex justify-content-end gap-2">
                    <Link
                      to={`/authuser/Edit-Land/${land.ln_id}`}
                      className="tf-btn bg-color-primary pd-20"
                    >
                      <i className="bi bi-pencil"></i>Edit
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </FrontendLayout>
  );
};

export default LandView;
